import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../App';
import JsBarcode from 'jsbarcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'general', hi: 'सामान्य', en: 'General' },
  { id: 'fiction', hi: 'कथा साहित्य', en: 'Fiction' },
  { id: 'non_fiction', hi: 'गैर-कथा', en: 'Non-Fiction' },
  { id: 'science', hi: 'विज्ञान', en: 'Science' },
  { id: 'mathematics', hi: 'गणित', en: 'Mathematics' },
  { id: 'history', hi: 'इतिहास', en: 'History' },
  { id: 'geography', hi: 'भूगोल', en: 'Geography' },
  { id: 'literature', hi: 'साहित्य', en: 'Literature' },
  { id: 'hindi', hi: 'हिंदी', en: 'Hindi' },
  { id: 'english', hi: 'अंग्रेज़ी', en: 'English' },
  { id: 'computer', hi: 'कंप्यूटर', en: 'Computer' },
  { id: 'textbook', hi: 'पाठ्यपुस्तक', en: 'Textbook' },
  { id: 'reference', hi: 'संदर्भ', en: 'Reference' },
  { id: 'biography', hi: 'जीवनी', en: 'Biography' },
];

const TABS = [
  { id: 'dashboard', hi: 'डैशबोर्ड', en: 'Dashboard', icon: '📊' },
  { id: 'books', hi: 'पुस्तकें', en: 'Books', icon: '📚' },
  { id: 'scanner', hi: 'बारकोड स्कैनर', en: 'Barcode Scanner', icon: '📷' },
  { id: 'issue', hi: 'जारी / वापसी', en: 'Issue / Return', icon: '🔄' },
  { id: 'analytics', hi: 'एनालिटिक्स', en: 'Analytics', icon: '📈' },
  { id: 'history', hi: 'इतिहास', en: 'History', icon: '📋' },
  { id: 'fines', hi: 'जुर्माना', en: 'Fines', icon: '💰' },
];

function api(endpoint, token, options = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_URL}${endpoint}`, { headers, ...options })
    .then(r => r.json())
    .catch(() => ({ success: false }));
}

export default function LibraryPage({ token }) {
  const { language } = useLanguage();
  const t = (hi, en) => language === 'hi' ? hi : en;
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="space-y-5" data-testid="library-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800" data-testid="library-heading">
          {t('📖 पुस्तकालय प्रबंधन', '📖 Library Management')}
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="library-tabs">
        {TABS.map(tb => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`library-tab-${tb.id}`}
            className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition flex items-center gap-1.5 ${
              tab === tb.id ? 'bg-orange-500 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
          >
            <span>{tb.icon}</span>
            <span>{t(tb.hi, tb.en)}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'dashboard' && <DashboardTab token={token} t={t} />}
      {tab === 'books' && <BooksTab token={token} t={t} />}
      {tab === 'scanner' && <BarcodeScannerTab token={token} t={t} />}
      {tab === 'issue' && <IssueReturnTab token={token} t={t} />}
      {tab === 'analytics' && <AnalyticsTab token={token} t={t} />}
      {tab === 'history' && <HistoryTab token={token} t={t} />}
      {tab === 'fines' && <FinesTab token={token} t={t} />}
    </div>
  );
}

/* ==================== DASHBOARD TAB ==================== */
function DashboardTab({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/library/dashboard', token).then(d => { setData(d); setLoading(false); });
  }, [token]);

  if (loading) return <Loader />;
  if (!data?.success) return <EmptyState msg={t('डैशबोर्ड लोड नहीं हुआ', 'Failed to load dashboard')} />;

  const s = data.stats;
  const stats = [
    { label: t('कुल पुस्तकें', 'Total Books'), value: s.total_books, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: t('कुल प्रतियाँ', 'Total Copies'), value: s.total_copies, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { label: t('उपलब्ध', 'Available'), value: s.available_copies, color: 'bg-green-50 text-green-700 border-green-200' },
    { label: t('जारी', 'Issued'), value: s.issued_copies, color: 'bg-orange-50 text-orange-700 border-orange-200' },
    { label: t('अतिदेय', 'Overdue'), value: s.overdue_count, color: 'bg-red-50 text-red-700 border-red-200' },
    { label: t('लंबित जुर्माना', 'Pending Fines'), value: `₹${s.pending_fines}`, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  ];

  return (
    <div className="space-y-5" data-testid="library-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((st, i) => (
          <div key={i} className={`p-3 rounded-xl border ${st.color}`} data-testid={`stat-card-${i}`}>
            <div className="text-xs opacity-75">{st.label}</div>
            <div className="text-xl font-bold mt-1">{st.value}</div>
          </div>
        ))}
      </div>

      {/* Category Breakdown */}
      {data.categories?.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold text-gray-800 mb-3">{t('श्रेणी अनुसार', 'By Category')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {data.categories.map((c, i) => (
              <div key={i} className="flex justify-between items-center py-1.5 px-3 bg-gray-50 rounded-lg text-sm">
                <span className="capitalize text-gray-700">{c.category?.replace('_', ' ')}</span>
                <span className="font-semibold text-gray-900">{c.books} ({c.copies})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {data.recent_activity?.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold text-gray-800 mb-3">{t('हालिया गतिविधि', 'Recent Activity')}</h3>
          <div className="space-y-2">
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                <div>
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${a.status === 'issued' ? 'bg-orange-500' : 'bg-green-500'}`}></span>
                  <span className="font-medium">{a.book_title}</span>
                  <span className="text-gray-500 ml-2">→ {a.student_name}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'issued' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                  {a.status === 'issued' ? t('जारी', 'Issued') : t('वापस', 'Returned')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== BOOKS TAB ==================== */
function BooksTab({ token, t }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [form, setForm] = useState({ title: '', author: '', isbn: '', category: 'general', publisher: '', edition: '', total_copies: 1, shelf_location: '', language: 'Hindi' });

  const load = useCallback(() => {
    let url = '/api/library/books?';
    if (catFilter !== 'all') url += `category=${catFilter}&`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api(url, token).then(d => { if (d.success) setBooks(d.books); setLoading(false); });
  }, [token, catFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = () => {
    api('/api/library/books', token, { method: 'POST', body: JSON.stringify(form) })
      .then(d => {
        if (d.success) { load(); setShowAdd(false); setForm({ title: '', author: '', isbn: '', category: 'general', publisher: '', edition: '', total_copies: 1, shelf_location: '', language: 'Hindi' }); }
      });
  };

  const handleDelete = (bookId) => {
    if (!window.confirm(t('क्या आप यह पुस्तक हटाना चाहते हैं?', 'Delete this book?'))) return;
    api(`/api/library/books/${bookId}`, token, { method: 'DELETE' }).then(d => { if (d.success) load(); });
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-4" data-testid="library-books-tab">
      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          data-testid="book-search-input"
          placeholder={t('🔍 खोजें...', '🔍 Search...')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm w-48"
        />
        <select
          data-testid="book-category-filter"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="all">{t('सभी श्रेणी', 'All Categories')}</option>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{t(c.hi, c.en)}</option>)}
        </select>
        <button
          data-testid="add-book-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="ml-auto px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition"
        >
          {showAdd ? t('✕ बंद करें', '✕ Cancel') : t('+ पुस्तक जोड़ें', '+ Add Book')}
        </button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="add-book-form">
          <h3 className="font-semibold text-gray-800">{t('नई पुस्तक जोड़ें', 'Add New Book')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <input data-testid="book-title-input" placeholder={t('पुस्तक का नाम *', 'Book Title *')} value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" required />
            <input data-testid="book-author-input" placeholder={t('लेखक', 'Author')} value={form.author} onChange={e => setForm({...form, author: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input data-testid="book-isbn-input" placeholder="ISBN" value={form.isbn} onChange={e => setForm({...form, isbn: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <select data-testid="book-category-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{t(c.hi, c.en)}</option>)}
            </select>
            <input placeholder={t('प्रकाशक', 'Publisher')} value={form.publisher} onChange={e => setForm({...form, publisher: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={t('संस्करण', 'Edition')} value={form.edition} onChange={e => setForm({...form, edition: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input data-testid="book-copies-input" type="number" min="1" placeholder={t('प्रतियाँ', 'Total Copies')} value={form.total_copies} onChange={e => setForm({...form, total_copies: parseInt(e.target.value) || 1})} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={t('शेल्फ स्थान', 'Shelf Location')} value={form.shelf_location} onChange={e => setForm({...form, shelf_location: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <select value={form.language} onChange={e => setForm({...form, language: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
              <option value="Hindi">{t('हिंदी', 'Hindi')}</option>
              <option value="English">{t('अंग्रेज़ी', 'English')}</option>
              <option value="Sanskrit">{t('संस्कृत', 'Sanskrit')}</option>
              <option value="Urdu">{t('उर्दू', 'Urdu')}</option>
            </select>
          </div>
          <button data-testid="save-book-btn" onClick={handleAdd} disabled={!form.title} className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {t('सहेजें', 'Save Book')}
          </button>
        </div>
      )}

      {/* Books Grid */}
      {books.length === 0 ? (
        <EmptyState msg={t('कोई पुस्तक नहीं मिली', 'No books found')} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="books-grid">
          {books.map(b => (
            <div key={b.book_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`book-card-${b.book_id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-800 truncate">{b.title}</h4>
                  <p className="text-sm text-gray-500 truncate">{b.author || t('अज्ञात लेखक', 'Unknown Author')}</p>
                </div>
                <button onClick={() => handleDelete(b.book_id)} className="text-red-400 hover:text-red-600 text-sm ml-2" data-testid={`delete-book-${b.book_id}`}>✕</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full capitalize">{b.category?.replace('_', ' ')}</span>
                {b.isbn && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{b.isbn}</span>}
                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">{b.language}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{t('प्रतियाँ:', 'Copies:')} {b.total_copies}</span>
                <span className={`font-medium ${b.available_copies > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {b.available_copies > 0 ? `${b.available_copies} ${t('उपलब्ध', 'available')}` : t('सभी जारी', 'All issued')}
                </span>
              </div>
              {b.shelf_location && <div className="mt-1 text-xs text-gray-400">{t('शेल्फ:', 'Shelf:')} {b.shelf_location}</div>}
              {/* Barcode */}
              {b.barcode && (
                <div className="mt-3 pt-3 border-t">
                  <BarcodeDisplay value={b.barcode} height={35} />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400 font-mono">{b.barcode}</span>
                    <button
                      data-testid={`print-barcode-${b.book_id}`}
                      onClick={() => printBarcode(b.barcode, b.title)}
                      className="text-xs text-orange-500 hover:text-orange-700 font-medium"
                    >
                      {t('प्रिंट', 'Print')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== ISSUE / RETURN TAB ==================== */
function IssueReturnTab({ token, t }) {
  const [mode, setMode] = useState('issue');
  const [books, setBooks] = useState([]);
  const [issuedTxns, setIssuedTxns] = useState([]);
  const [form, setForm] = useState({ book_id: '', student_name: '', student_id: '', class_name: '', section: '', due_days: 14 });
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api('/api/library/books', token).then(d => { if (d.success) setBooks(d.books); });
    api('/api/library/transactions?status=issued', token).then(d => { if (d.success) setIssuedTxns(d.transactions); });
  }, [token]);

  const handleIssue = () => {
    if (!form.book_id || !form.student_name || !form.student_id) return;
    api('/api/library/issue', token, { method: 'POST', body: JSON.stringify(form) })
      .then(d => {
        setMsg(d.message || (d.success ? t('पुस्तक जारी हुई', 'Book issued') : d.detail || t('त्रुटि', 'Error')));
        if (d.success) {
          setForm({ book_id: '', student_name: '', student_id: '', class_name: '', section: '', due_days: 14 });
          // Refresh
          api('/api/library/books', token).then(r => { if (r.success) setBooks(r.books); });
          api('/api/library/transactions?status=issued', token).then(r => { if (r.success) setIssuedTxns(r.transactions); });
        }
        setTimeout(() => setMsg(''), 3000);
      });
  };

  const handleReturn = (txnId) => {
    api('/api/library/return', token, { method: 'POST', body: JSON.stringify({ transaction_id: txnId }) })
      .then(d => {
        setMsg(d.message || t('पुस्तक वापस हुई', 'Book returned'));
        api('/api/library/transactions?status=issued', token).then(r => { if (r.success) setIssuedTxns(r.transactions); });
        api('/api/library/books', token).then(r => { if (r.success) setBooks(r.books); });
        setTimeout(() => setMsg(''), 3000);
      });
  };

  return (
    <div className="space-y-4" data-testid="library-issue-tab">
      {msg && <div className="p-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium" data-testid="issue-message">{msg}</div>}

      <div className="flex gap-2">
        <button data-testid="mode-issue-btn" onClick={() => setMode('issue')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'issue' ? 'bg-orange-500 text-white' : 'bg-white border text-gray-600'}`}>
          {t('📗 पुस्तक जारी करें', '📗 Issue Book')}
        </button>
        <button data-testid="mode-return-btn" onClick={() => setMode('return')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'return' ? 'bg-green-500 text-white' : 'bg-white border text-gray-600'}`}>
          {t('📕 पुस्तक वापसी', '📕 Return Book')}
        </button>
      </div>

      {mode === 'issue' && (
        <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="issue-form">
          <h3 className="font-semibold text-gray-800">{t('पुस्तक जारी करें', 'Issue Book')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <select data-testid="issue-book-select" value={form.book_id} onChange={e => setForm({...form, book_id: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
              <option value="">{t('पुस्तक चुनें *', 'Select Book *')}</option>
              {books.filter(b => b.available_copies > 0).map(b => (
                <option key={b.book_id} value={b.book_id}>{b.title} ({b.available_copies} {t('उपलब्ध', 'avail.')})</option>
              ))}
            </select>
            <input data-testid="issue-student-name" placeholder={t('छात्र का नाम *', 'Student Name *')} value={form.student_name} onChange={e => setForm({...form, student_name: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input data-testid="issue-student-id" placeholder={t('छात्र ID *', 'Student ID *')} value={form.student_id} onChange={e => setForm({...form, student_id: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={t('कक्षा', 'Class')} value={form.class_name} onChange={e => setForm({...form, class_name: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={t('सेक्शन', 'Section')} value={form.section} onChange={e => setForm({...form, section: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
            <input type="number" min="1" max="90" placeholder={t('दिन (डिफ़ॉल्ट 14)', 'Due Days (default 14)')} value={form.due_days} onChange={e => setForm({...form, due_days: parseInt(e.target.value) || 14})} className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button data-testid="issue-submit-btn" onClick={handleIssue} disabled={!form.book_id || !form.student_name || !form.student_id} className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {t('जारी करें', 'Issue Book')}
          </button>
        </div>
      )}

      {mode === 'return' && (
        <div className="bg-white rounded-xl border p-4" data-testid="return-section">
          <h3 className="font-semibold text-gray-800 mb-3">{t('जारी पुस्तकें — वापसी करें', 'Issued Books — Return')}</h3>
          {issuedTxns.length === 0 ? (
            <EmptyState msg={t('कोई जारी पुस्तक नहीं', 'No issued books')} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('पुस्तक', 'Book')}</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('छात्र', 'Student')}</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('जारी तिथि', 'Issue Date')}</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('देय तिथि', 'Due Date')}</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('स्थिति', 'Status')}</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {issuedTxns.map(txn => {
                    const isOverdue = new Date(txn.due_date) < new Date();
                    return (
                      <tr key={txn.transaction_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{txn.book_title}</td>
                        <td className="px-3 py-2">{txn.student_name} <span className="text-xs text-gray-400">{txn.student_id}</span></td>
                        <td className="px-3 py-2 text-center text-xs">{new Date(txn.issue_date).toLocaleDateString('hi-IN')}</td>
                        <td className={`px-3 py-2 text-center text-xs ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>{new Date(txn.due_date).toLocaleDateString('hi-IN')}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                            {isOverdue ? t('अतिदेय', 'Overdue') : t('जारी', 'Issued')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            data-testid={`return-btn-${txn.transaction_id}`}
                            onClick={() => handleReturn(txn.transaction_id)}
                            className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition"
                          >
                            {t('वापस करें', 'Return')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==================== HISTORY TAB ==================== */
function HistoryTab({ token, t }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let url = '/api/library/transactions?';
    if (statusFilter !== 'all') url += `status=${statusFilter}&`;
    api(url, token).then(d => { if (d.success) setTxns(d.transactions); setLoading(false); });
  }, [token, statusFilter]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-4" data-testid="library-history-tab">
      <div className="flex gap-2">
        {['all', 'issued', 'returned'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === s ? 'bg-orange-500 text-white' : 'bg-white border text-gray-600'}`} data-testid={`history-filter-${s}`}>
            {s === 'all' ? t('सभी', 'All') : s === 'issued' ? t('जारी', 'Issued') : t('वापस', 'Returned')}
          </button>
        ))}
      </div>

      {txns.length === 0 ? (
        <EmptyState msg={t('कोई रिकॉर्ड नहीं', 'No records')} />
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('पुस्तक', 'Book')}</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('छात्र', 'Student')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('जारी', 'Issued')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('वापसी', 'Returned')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('जुर्माना', 'Fine')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('स्थिति', 'Status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {txns.map(txn => (
                <tr key={txn.transaction_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{txn.book_title}</td>
                  <td className="px-3 py-2">{txn.student_name}</td>
                  <td className="px-3 py-2 text-center text-xs">{new Date(txn.issue_date).toLocaleDateString('hi-IN')}</td>
                  <td className="px-3 py-2 text-center text-xs">{txn.return_date ? new Date(txn.return_date).toLocaleDateString('hi-IN') : '-'}</td>
                  <td className="px-3 py-2 text-center">{txn.fine_amount > 0 ? `₹${txn.fine_amount}` : '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${txn.status === 'issued' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                      {txn.status === 'issued' ? t('जारी', 'Issued') : t('वापस', 'Returned')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== FINES TAB ==================== */
function FinesTab({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api('/api/library/fines', token).then(d => { setData(d); setLoading(false); });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handlePay = (fineId) => {
    api(`/api/library/fines/${fineId}/pay`, token, { method: 'PUT' }).then(d => { if (d.success) load(); });
  };

  if (loading) return <Loader />;
  if (!data?.success) return <EmptyState msg={t('डेटा लोड नहीं हुआ', 'Failed to load')} />;

  return (
    <div className="space-y-4" data-testid="library-fines-tab">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="text-xs text-yellow-600">{t('लंबित जुर्माना', 'Pending Fines')}</div>
          <div className="text-xl font-bold text-yellow-700">₹{data.total_pending}</div>
        </div>
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
          <div className="text-xs text-green-600">{t('वसूला गया', 'Collected')}</div>
          <div className="text-xl font-bold text-green-700">₹{data.total_collected}</div>
        </div>
      </div>

      {data.fines?.length === 0 ? (
        <EmptyState msg={t('कोई जुर्माना नहीं', 'No fines')} />
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('छात्र', 'Student')}</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('पुस्तक', 'Book')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('दिन', 'Days')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('राशि', 'Amount')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('स्थिति', 'Status')}</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.fines.map(f => (
                <tr key={f.fine_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{f.student_name}</td>
                  <td className="px-3 py-2">{f.book_title}</td>
                  <td className="px-3 py-2 text-center">{f.overdue_days}</td>
                  <td className="px-3 py-2 text-center font-semibold text-red-600">₹{f.fine_amount}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${f.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {f.status === 'paid' ? t('भुगतान हुआ', 'Paid') : t('लंबित', 'Pending')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {f.status === 'pending' && (
                      <button data-testid={`pay-fine-${f.fine_id}`} onClick={() => handlePay(f.fine_id)} className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition">
                        {t('भुगतान करें', 'Pay')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== ANALYTICS TAB ==================== */
const CHART_COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#f59e0b', '#06b6d4', '#ec4899', '#6366f1', '#14b8a6'];

function AnalyticsTab({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/library/analytics', token).then(d => { setData(d); setLoading(false); });
  }, [token]);

  if (loading) return <Loader />;
  if (!data?.success) return <EmptyState msg={t('एनालिटिक्स लोड नहीं हुआ', 'Failed to load analytics')} />;

  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="library-analytics-tab">
      {/* Export Buttons */}
      <div className="flex flex-wrap gap-2 justify-end">
        <button
          data-testid="export-pdf-btn"
          onClick={() => {
            const link = document.createElement('a');
            link.href = `${API_URL}/api/library/analytics/pdf`;
            link.setAttribute('download', '');
            // Add auth token
            fetch(`${API_URL}/api/library/analytics/pdf`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => res.blob()).then(blob => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Library_Report_${new Date().toISOString().slice(0,10)}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            });
          }}
          className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          {t('PDF डाउनलोड करें', 'Download PDF')}
        </button>
        <button
          data-testid="share-report-whatsapp-btn"
          onClick={() => {
            const msg = `${t('📚 पुस्तकालय रिपोर्ट', '📚 Library Report')} - ${new Date().toLocaleDateString('hi-IN')}%0A%0A` +
              `${t('कुल पुस्तकें', 'Total Books')}: ${s.total_transactions}%0A` +
              `${t('वर्तमान में जारी', 'Currently Issued')}: ${s.total_active}%0A` +
              `${t('वापस हुई', 'Returned')}: ${s.total_returned}%0A` +
              `${t('कुल पाठक', 'Unique Readers')}: ${s.unique_readers}%0A%0A` +
              (data.popular_books?.length > 0
                ? `${t('लोकप्रिय पुस्तक', 'Popular Book')}: ${data.popular_books[0].title} (${data.popular_books[0].issue_count}x)%0A`
                : '') +
              `%0A- शिक्षा मित्र`;
            window.open(`https://wa.me/?text=${msg}`, '_blank');
          }}
          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.644-1.217A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.17 0-4.207-.654-5.893-1.776l-.422-.254-4.377 1.147 1.168-4.265-.279-.443A9.706 9.706 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
          {t('WhatsApp पर भेजें', 'Share on WhatsApp')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl" data-testid="analytics-total-txns">
          <div className="text-xs text-orange-500">{t('कुल लेन-देन', 'Total Transactions')}</div>
          <div className="text-2xl font-bold text-orange-700 mt-1">{s.total_transactions}</div>
        </div>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="analytics-active">
          <div className="text-xs text-blue-500">{t('वर्तमान में जारी', 'Currently Issued')}</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{s.total_active}</div>
        </div>
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="analytics-returned">
          <div className="text-xs text-green-500">{t('वापस हुई', 'Returned')}</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{s.total_returned}</div>
        </div>
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="analytics-readers">
          <div className="text-xs text-purple-500">{t('कुल पाठक', 'Unique Readers')}</div>
          <div className="text-2xl font-bold text-purple-700 mt-1">{s.unique_readers}</div>
        </div>
      </div>

      {/* Popular Books */}
      {data.popular_books?.length > 0 && (
        <div className="bg-white rounded-xl border p-4" data-testid="popular-books-section">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-orange-500">&#9733;</span>
            {t('सबसे लोकप्रिय पुस्तकें', 'Most Popular Books')}
          </h3>
          <div className="space-y-2">
            {data.popular_books.map((b, i) => {
              const maxCount = data.popular_books[0]?.issue_count || 1;
              const pct = Math.round((b.issue_count / maxCount) * 100);
              return (
                <div key={b.book_id} className="flex items-center gap-3" data-testid={`popular-book-${i}`}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="truncate">
                        <span className="font-semibold text-sm text-gray-800">{b.title}</span>
                        {b.author && <span className="text-xs text-gray-400 ml-2">{b.author}</span>}
                      </div>
                      <span className="text-sm font-bold text-orange-600 ml-2 whitespace-nowrap">{b.issue_count}x</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
                      <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    </div>
                    <div className="flex gap-2 mt-0.5">
                      {b.category && <span className="text-xs text-gray-400 capitalize">{b.category.replace('_', ' ')}</span>}
                      <span className="text-xs text-gray-400">{t('उपलब्ध:', 'Avail:')} {b.available}/{b.total}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Class-wise Reading */}
        {data.class_trends?.length > 0 && (
          <div className="bg-white rounded-xl border p-4" data-testid="class-trends-section">
            <h3 className="font-bold text-gray-800 mb-3">{t('कक्षा अनुसार पठन', 'Class-wise Reading')}</h3>
            <div className="space-y-2">
              {data.class_trends.map((c, i) => {
                const maxIssue = data.class_trends[0]?.issue_count || 1;
                const pct = Math.round((c.issue_count / maxIssue) * 100);
                return (
                  <div key={c.class_name} data-testid={`class-trend-${i}`}>
                    <div className="flex justify-between text-sm mb-0.5">
                      <span className="font-medium text-gray-700">{c.class_name}</span>
                      <span className="text-gray-500">{c.issue_count} {t('पुस्तकें', 'books')} | {c.student_count} {t('छात्र', 'students')}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Category Distribution */}
        {data.category_distribution?.length > 0 && (
          <div className="bg-white rounded-xl border p-4" data-testid="category-distribution-section">
            <h3 className="font-bold text-gray-800 mb-3">{t('श्रेणी अनुसार जारी', 'Issues by Category')}</h3>
            <div className="space-y-2">
              {data.category_distribution.map((c, i) => {
                const total = data.category_distribution.reduce((acc, x) => acc + x.issue_count, 0);
                const pct = total > 0 ? Math.round((c.issue_count / total) * 100) : 0;
                return (
                  <div key={c.category} className="flex items-center gap-2" data-testid={`cat-dist-${i}`}>
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <div className="flex-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize text-gray-700">{c.category.replace('_', ' ')}</span>
                        <span className="text-gray-500">{c.issue_count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 mt-0.5">
                        <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Trends */}
      {data.monthly_trends?.length > 0 && (
        <div className="bg-white rounded-xl border p-4" data-testid="monthly-trends-section">
          <h3 className="font-bold text-gray-800 mb-3">{t('मासिक पठन रुझान', 'Monthly Reading Trends')}</h3>
          <div className="flex items-end gap-1 h-40">
            {data.monthly_trends.map((m, i) => {
              const maxVal = Math.max(...data.monthly_trends.map(x => x.issues), 1);
              const barH = Math.max((m.issues / maxVal) * 100, 4);
              const retH = maxVal > 0 ? Math.max((m.returns / maxVal) * 100, 0) : 0;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" data-testid={`monthly-bar-${i}`}>
                  <div className="text-xs font-semibold text-orange-600">{m.issues}</div>
                  <div className="w-full flex flex-col items-center" style={{ height: '120px', justifyContent: 'flex-end' }}>
                    <div className="w-full max-w-[28px] bg-orange-400 rounded-t-sm transition-all duration-500" style={{ height: `${barH}%` }} title={`${t('जारी:', 'Issues:')} ${m.issues}`} />
                    {retH > 0 && (
                      <div className="w-full max-w-[28px] bg-green-400 rounded-b-sm transition-all duration-500" style={{ height: `${retH}%` }} title={`${t('वापसी:', 'Returns:')} ${m.returns}`} />
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 -rotate-45 origin-top-left whitespace-nowrap">{m.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-400 rounded-sm inline-block" /> {t('जारी', 'Issued')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded-sm inline-block" /> {t('वापस', 'Returned')}</span>
          </div>
        </div>
      )}

      {/* Top Readers */}
      {data.top_readers?.length > 0 && (
        <div className="bg-white rounded-xl border p-4" data-testid="top-readers-section">
          <h3 className="font-bold text-gray-800 mb-3">{t('सबसे ज़्यादा पढ़ने वाले छात्र', 'Top Readers')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">{t('छात्र', 'Student')}</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('कक्षा', 'Class')}</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('कुल पुस्तकें', 'Total Books')}</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">{t('वर्तमान में', 'Currently')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.top_readers.map((r, i) => (
                  <tr key={r.student_id} className="hover:bg-gray-50" data-testid={`top-reader-${i}`}>
                    <td className="px-3 py-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                        {i + 1}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{r.student_name}</span>
                      <span className="text-xs text-gray-400 ml-1">{r.student_id}</span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">{r.class_name || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-bold text-orange-600">{r.books_borrowed}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.currently_holding > 0 ? (
                        <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">{r.currently_holding} {t('जारी', 'issued')}</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state if no data */}
      {data.popular_books?.length === 0 && data.top_readers?.length === 0 && (
        <div className="text-center py-16 text-gray-400" data-testid="analytics-empty">
          <div className="text-5xl mb-3">📈</div>
          <p className="text-lg">{t('अभी कोई डेटा नहीं है', 'No analytics data yet')}</p>
          <p className="text-sm mt-1">{t('पुस्तकें जारी करें और यहाँ रुझान देखें', 'Issue books to see trends here')}</p>
        </div>
      )}
    </div>
  );
}

/* ==================== HELPERS ==================== */
function Loader() {
  return (
    <div className="flex justify-center items-center h-40" data-testid="library-loader">
      <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="text-center py-12 text-gray-400" data-testid="library-empty">
      <div className="text-4xl mb-2">📚</div>
      <p>{msg}</p>
    </div>
  );
}

/* ==================== BARCODE DISPLAY ==================== */
function BarcodeDisplay({ value, height = 40 }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (svgRef.current && value) {
      try {
        JsBarcode(svgRef.current, value, {
          format: 'CODE128',
          height: height,
          displayValue: false,
          margin: 2,
          width: 1.5,
          background: 'transparent',
        });
      } catch (e) {
        console.warn('Barcode render error:', e);
      }
    }
  }, [value, height]);

  return <svg ref={svgRef} data-testid={`barcode-svg-${value}`} className="w-full" />;
}

/* ==================== PRINT BARCODE ==================== */
function printBarcode(barcode, title) {
  const printWin = window.open('', '_blank', 'width=400,height=300');
  if (!printWin) return;
  printWin.document.write(`
    <html><head><title>Barcode - ${title}</title>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.3/dist/JsBarcode.all.min.js"><\/script>
    <style>
      body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; }
      .label { text-align: center; padding: 16px; border: 1px dashed #ccc; }
      .title { font-size: 12px; font-weight: 600; margin-bottom: 8px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .code { font-size: 10px; color: #888; margin-top: 4px; font-family: monospace; }
    </style></head>
    <body>
      <div class="label">
        <div class="title">${title}</div>
        <svg id="bc"></svg>
        <div class="code">${barcode}</div>
      </div>
      <script>
        JsBarcode("#bc", "${barcode}", { format: "CODE128", height: 50, displayValue: false, margin: 4, width: 2 });
        setTimeout(() => { window.print(); window.close(); }, 500);
      <\/script>
    </body></html>
  `);
  printWin.document.close();
}

/* ==================== BARCODE SCANNER TAB ==================== */
function BarcodeScannerTab({ token, t }) {
  const [scanResult, setScanResult] = useState(null);
  const [lookupBook, setLookupBook] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const lookupBarcode = useCallback((code) => {
    setScanResult(code);
    setLookupBook(null);
    setError('');
    api(`/api/library/books/barcode/${encodeURIComponent(code)}`, token)
      .then(d => {
        if (d.success) {
          setLookupBook(d.book);
          setMsg(t('पुस्तक मिली!', 'Book found!'));
        } else {
          setError(t('इस बारकोड की कोई पुस्तक नहीं मिली', 'No book found for this barcode'));
        }
        setTimeout(() => setMsg(''), 3000);
      });
  }, [token, t]);

  const startScanner = useCallback(async () => {
    if (html5QrCodeRef.current) return;
    setScanning(true);
    setError('');
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("barcode-reader");
      html5QrCodeRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 150 } },
        (decodedText) => {
          lookupBarcode(decodedText);
          stopScanner();
        },
        () => {}
      );
    } catch (err) {
      setError(t('कैमरा शुरू नहीं हो सका। कृपया मैन्युअल कोड डालें।', 'Camera failed to start. Please enter code manually.'));
      setScanning(false);
    }
  }, [lookupBarcode, t]);

  const stopScanner = useCallback(() => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current.clear().catch(() => {});
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, [stopScanner]);

  const handleQuickIssue = (bookId) => {
    const name = prompt(t('छात्र का नाम:', 'Student Name:'));
    const sid = prompt(t('छात्र ID:', 'Student ID:'));
    if (!name || !sid) return;
    api('/api/library/issue', token, {
      method: 'POST',
      body: JSON.stringify({ book_id: bookId, student_name: name, student_id: sid, due_days: 14 })
    }).then(d => {
      setMsg(d.message || (d.success ? t('पुस्तक जारी हुई!', 'Book issued!') : d.detail || t('त्रुटि', 'Error')));
      if (d.success) { lookupBarcode(scanResult); }
      setTimeout(() => setMsg(''), 3000);
    });
  };

  return (
    <div className="space-y-4" data-testid="library-scanner-tab">
      {msg && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm font-medium" data-testid="scanner-message">{msg}</div>}
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium" data-testid="scanner-error">{error}</div>}

      {/* Scanner Area */}
      <div className="bg-white rounded-xl border p-5" data-testid="scanner-area">
        <h3 className="font-semibold text-gray-800 mb-4 text-center">{t('📷 बारकोड स्कैन करें', '📷 Scan Barcode')}</h3>

        <div className="flex justify-center gap-3 mb-4">
          {!scanning ? (
            <button
              data-testid="start-scan-btn"
              onClick={startScanner}
              className="px-5 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition flex items-center gap-2"
            >
              <span className="text-lg">📷</span>
              {t('कैमरा से स्कैन करें', 'Scan with Camera')}
            </button>
          ) : (
            <button
              data-testid="stop-scan-btn"
              onClick={stopScanner}
              className="px-5 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition"
            >
              {t('स्कैनर बंद करें', 'Stop Scanner')}
            </button>
          )}
        </div>

        {/* Camera viewfinder */}
        <div
          id="barcode-reader"
          ref={scannerRef}
          className={`mx-auto max-w-md rounded-lg overflow-hidden ${scanning ? 'border-2 border-orange-300' : ''}`}
          style={{ minHeight: scanning ? '250px' : '0' }}
        />

        {/* Manual Input */}
        <div className="mt-4 flex gap-2 items-center justify-center">
          <span className="text-sm text-gray-500">{t('या मैन्युअल डालें:', 'Or enter manually:')}</span>
          <input
            data-testid="manual-barcode-input"
            placeholder={t('बारकोड कोड', 'Barcode code')}
            value={manualCode}
            onChange={e => setManualCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && manualCode) lookupBarcode(manualCode); }}
            className="px-3 py-2 border rounded-lg text-sm w-40 font-mono"
          />
          <button
            data-testid="manual-lookup-btn"
            onClick={() => { if (manualCode) lookupBarcode(manualCode); }}
            disabled={!manualCode}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition"
          >
            {t('खोजें', 'Lookup')}
          </button>
        </div>
      </div>

      {/* Scan Result */}
      {lookupBook && (
        <div className="bg-white rounded-xl border p-5" data-testid="scan-result">
          <div className="flex items-start gap-4">
            <div className="w-32 flex-shrink-0">
              <BarcodeDisplay value={lookupBook.barcode} height={45} />
              <div className="text-center text-xs font-mono text-gray-400 mt-1">{lookupBook.barcode}</div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-gray-800">{lookupBook.title}</h3>
              <p className="text-sm text-gray-500">{lookupBook.author}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full capitalize">{lookupBook.category?.replace('_', ' ')}</span>
                {lookupBook.isbn && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{lookupBook.isbn}</span>}
                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">{lookupBook.language}</span>
              </div>
              <div className="mt-2 flex gap-4 text-sm">
                <span>{t('कुल:', 'Total:')} <strong>{lookupBook.total_copies}</strong></span>
                <span className="text-green-600">{t('उपलब्ध:', 'Available:')} <strong>{lookupBook.available_copies}</strong></span>
                <span className="text-orange-600">{t('जारी:', 'Issued:')} <strong>{lookupBook.issued_copies}</strong></span>
              </div>
              {lookupBook.shelf_location && <div className="text-xs text-gray-400 mt-1">{t('शेल्फ:', 'Shelf:')} {lookupBook.shelf_location}</div>}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {lookupBook.available_copies > 0 && (
              <button
                data-testid="quick-issue-btn"
                onClick={() => handleQuickIssue(lookupBook.book_id)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition"
              >
                {t('तुरंत जारी करें', 'Quick Issue')}
              </button>
            )}
            <button
              onClick={() => printBarcode(lookupBook.barcode, lookupBook.title)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
            >
              {t('बारकोड प्रिंट करें', 'Print Barcode')}
            </button>
            <button
              onClick={() => { setScanResult(null); setLookupBook(null); setManualCode(''); }}
              className="px-4 py-2 bg-gray-50 text-gray-500 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
            >
              {t('फिर से स्कैन करें', 'Scan Again')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
